import { request } from './http';
import {
  AnalyticsRange,
  GrowthMilestonesResponse,
  GoalAnalyticsResponse,
  PortfolioAnalyticsResponse,
  ProjectAnalyticsResponse,
  RealtimeAnalyticsResponse,
  RegistrationTrackingStatusResponse,
  RevenueAnalyticsResponse,
  RevenueMode,
  RevenueProvider,
  SearchConsoleAnalyticsResponse,
  ExternalSignalsResponse,
} from './types';
import {
  analyticsRangeParams,
  type AnalyticsRangeInput,
} from './analytics-range';

const checkRange = <T extends { range: AnalyticsRange }>(
  result: T,
  input: AnalyticsRangeInput,
): T => {
  const key = typeof input === 'string' ? input : input.key;
  if (result.range.key !== key) {
    throw new Error(
      'This date range needs the latest analytics API. Deploy the backend and try again.',
    );
  }
  return result;
};

export const getMilestones = (projectId: string, range: AnalyticsRange) =>
  request<GrowthMilestonesResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}?view=milestones&${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => {
    if (!Array.isArray(result.points)) {
      throw new Error('Milestone endpoint is not available yet');
    }
    return result;
  });

export const getGoalAnalytics = (
  projectId: string,
  goalId: string,
  range: AnalyticsRangeInput = '7d',
) =>
  request<GoalAnalyticsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/goals/${encodeURIComponent(goalId)}?${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => checkRange(result, range));

export const getPortfolio = (range: AnalyticsRangeInput = '7d') =>
  request<PortfolioAnalyticsResponse>(
    `/analytics/portfolio?${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => checkRange(result, range));

export const getProject = (
  projectId: string,
  range: AnalyticsRangeInput = '7d',
) =>
  request<ProjectAnalyticsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}?${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => checkRange(result, range));

export const getRealtime = (projectId: string) =>
  request<RealtimeAnalyticsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/realtime`,
    'GET',
  );

export const getRegistrationTrackingStatus = (projectId: string) =>
  request<RegistrationTrackingStatusResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/registrations`,
    'GET',
  );

export const getRevenue = (
  projectId: string,
  range: AnalyticsRangeInput = '7d',
  mode: RevenueMode = 'live',
  provider: RevenueProvider = 'stripe',
) =>
  request<RevenueAnalyticsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/revenue?${analyticsRangeParams(range)}&mode=${encodeURIComponent(mode)}&provider=${encodeURIComponent(provider)}`,
    'GET',
  ).then((result) => checkRange(result, range));

export const getSearchConsole = (
  projectId: string,
  range: AnalyticsRangeInput = '7d',
) =>
  request<SearchConsoleAnalyticsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/search-console?${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => checkRange(result, range));

export const getExternalSignals = (
  projectId: string,
  range: AnalyticsRangeInput = '7d',
) =>
  request<ExternalSignalsResponse>(
    `/analytics/project/${encodeURIComponent(projectId)}/external-signals?${analyticsRangeParams(range)}`,
    'GET',
  ).then((result) => checkRange(result, range));
